import React, { useEffect, useState, useRef } from 'react';
import { useGlobalContext } from '../hooks';
import { useIdleTimer } from 'react-idle-timer'
import { Modal } from '@amzn/stencil-react-components/modal';
import { H3, Text } from '@amzn/stencil-react-components/text';
import { Col } from '@amzn/stencil-react-components/layout';
import { useTranslation } from 'react-i18next';
import {getStage, isAftxURL} from "src/constants/stageConfiguration";

// Number of seconds until warning message is displayed
const IDLE_SECONDS_UNTIL_WARNING = 30;
// Number of /further/ seconds until user is logged out
// Note: The total elapsed seconds until a user is logged out will be:
// IDLE_SECONDS_UNTIL_WARNING + IDLE_SECONDS_UNTIL_LOGOUT
const IDLE_SECONDS_UNTIL_LOGOUT = 10;

interface WarningMessageProps {
  isPromptVisible: boolean;
  logUserOut: () => void;
}

/**
 * Helper component to AutoLogoutBoundary for managing the warning modal.
 *
 * Note: The useIdleTimer hook has props for promptTimeout and onPrompt which could be used to
 * eliminate the need for this separate WarningMessage component and the second timer (as shown below).
 * However, this approach would require us to specify a value for the onAction prop to reset the (single) timer.
 * The onAction prop would be called on every event which may not be ideal for performance.
 *
 * The approach chosen rather uses two timers since it allow the "onActive" callback to be used which is
 * only called once after the user is active after being idle (i.e. after the user is active
 * after the warning modal is displayed).
 *
 * Alternate Approach:
 * -------------------
 * export const AutoLogoutBoundary = ({ children }: any) => {
 *   const [isPromptVisible, setPromptVisible] = useState(false);
 *   const { isLoggedIn, setUserData } = useGlobalContext();
 *
 *   const onIdle = () => {
 *     setUserData('', null);
 *     setPromptVisible(false);
 *   }
 *   const onAction = (_event: Event|undefined) => {
 *     idleTimer.reset();
 *     idleTimer.start();
 *     if (isPromptVisible) {
 *       setPromptVisible(false);
 *     }
 *   }
 *   const onPrompt = () => {
 *     setPromptVisible(true);
 *   }
 *   const idleTimer = useIdleTimer({
 *     onIdle, // callback when promptTimeout is exceeded
 *     onAction, // callback whenever the user is active
 *     onPrompt, // callback when timeout is exceeded
 *     promptTimeout: 15000, // time in milliseconds of inactivity since onPrompt was called until calling onIdle
 *     timeout: 60000, // time in milliseconds of inactivity until calling onPrompt
 *     startManually: true, // we only want to manually start the timer
 *     debounce: 250, // reduce frequency at which onAction is called
 *   });
 *
 *   useEffect(() => {
 *     if (isLoggedIn) {
 *       setPromptVisible(false);
 *       idleTimer.reset();
 *       idleTimer.start();
 *     } else {
 *       idleTimer.pause();
 *     }
 *   }, [isLoggedIn]);
 *
 *   return (<>
 *    <Modal isOpen={isPromptVisible} close={() => {}}>
 *       ...
 *    </Modal>
 *    {children}
 *   </>);
 * }
 */
const WarningMessage = (props: WarningMessageProps) => {
  const { t } = useTranslation();
  const idleTimer = useIdleTimer({
    onIdle: props.logUserOut, // callback when timeout is exceeded
    timeout: IDLE_SECONDS_UNTIL_LOGOUT * 1000, // time in milliseconds of inactivity until calling onIdle
    startManually: true, // we only want to manually start the timer
  });

  // We only want to run the timer when the prompt is visible
  useEffect(() => {
    if (props.isPromptVisible) {
      idleTimer.reset();
      idleTimer.start();
    } else {
      idleTimer.pause();
    }
  }, [props.isPromptVisible]);

  return (
    <Text>
      <Modal isOpen={props.isPromptVisible} close={() => {}}>
        <Col
          backgroundColor="neutral0"
          gridGap="S500"
          padding="S500"
          alignItems="center"
        >
          <H3>{t('logoutTitle')}</H3>
          <Text>
            {t('logoutMessage', { seconds: IDLE_SECONDS_UNTIL_LOGOUT })}
          </Text>
        </Col>
      </Modal>
    </Text>
  );
}

/**
 * Component that will display a warning message modal after a certain period of idle time.
 * After another period of idle time the user will then be logged out.
 */
export const AutoLogoutBoundary = ({ children }: any) => {
  const [isPromptVisible, setPromptVisible] = useState(false);
  const { isLoggedIn, setUserData } = useGlobalContext();
  const stage = getStage();

  const logUserOut = () => {
    // 'loginType=moc' is used on the Login.tsx page to determine which FcMenu endpoint to use for the Login Button.
    window.location.href = `https://${stage}.pak.qubit.amazon.dev/${isAftxURL() ? '?loginType=moc' : ''}`; 
    setUserData('', null);
    setPromptVisible(false);
  }

  // Display warning message when user is idle
  const onIdle = () => {
    setPromptVisible(true);
  }

  // Hide the modal and reset timer
  const onActive = () => {
    idleTimer.reset();
    idleTimer.start();
    setPromptVisible(false);
  }

  const idleTimer = useIdleTimer({
    onIdle, // callback when timeout is exceeded
    onActive, // callback when user is active after being idle
    timeout: IDLE_SECONDS_UNTIL_WARNING * 1000, // time in milliseconds of inactivity until calling onIdle
    startManually: true, // we only want to manually start the timer
  });

  /**
   * We only want to run the timer when the user is logged in,
   * otherwise the warning prompt will display on the Login page.
   */
  useEffect(() => {
    if (isLoggedIn) {
      setPromptVisible(false);
      idleTimer.reset();
      idleTimer.start();
    } else {
      idleTimer.pause();
    }
  }, [isLoggedIn]);

  return (<>
    <WarningMessage isPromptVisible={isPromptVisible} logUserOut={logUserOut} />
    {children}
  </>);
};
